import React, { useState, useEffect, useRef } from "react";
import { API } from "../api";
import { ChevronDown, X, Search, MapPin, ShieldCheck, Database } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { DropdownPortal } from "./DropdownPortal";
import { fetchStates } from "../lib/hooks";

// Dropdown Component
const SimpleDropdown = ({
  placeholder,
  value,
  onChange,
  options,
  disabled = false,
  loading = false,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close on a click anywhere else. The list is portalled out of this subtree,
  // so both the trigger and the list count as inside.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const display = value ? `${value.name} (${value.code})` : "";

  const filteredOptions = options.filter(
    (opt) =>
      opt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      opt.code.toLowerCase().startsWith(searchQuery.toLowerCase())
  );

  return (
    <div className="relative w-full" ref={triggerRef}>
      <div
        className={`flex items-center justify-between w-full px-5 py-4 border-2 rounded-2xl select-none transition-all duration-75
          ${disabled
            ? "bg-slate-50 cursor-not-allowed opacity-50 border-slate-100"
            : isOpen
              ? "bg-white cursor-pointer border-green-500 shadow-lg shadow-green-300/70"
              : "bg-green-50 cursor-pointer border-green-100 shadow-md shadow-green-200/80 hover:bg-green-100 hover:shadow-lg hover:shadow-green-300/70"
          }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`text-sm font-semibold truncate ${value ? "text-slate-800" : "text-slate-400"}`}>
          {display || placeholder}
        </span>
        <div className="flex items-center gap-2">
          {value && !disabled && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
                setIsOpen(false);
              }}
              className="p-1 hover:bg-red-50 rounded-full text-slate-400 hover:text-red-500 transition-colors"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown className="w-4 h-4 text-green-600" />
        </div>
      </div>

      {isOpen && !disabled && (
        <DropdownPortal open={isOpen} targetRef={triggerRef}>
          <div ref={menuRef} className="bg-white border-2 border-green-100 rounded-[2rem] shadow-2xl mt-1 overflow-hidden z-[999] min-w-[var(--radix-popper-anchor-width)]">
            <div className="relative p-4 border-b border-slate-50 bg-slate-50/50">
              <Search className="absolute left-7 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
              <input
                type="text"
                placeholder="Type to filter..."
                className="w-full pl-10 pr-4 py-2 text-sm font-bold border-none bg-white rounded-xl focus:outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="max-h-64 overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="px-4 py-10 text-center text-slate-400 text-xs font-black uppercase tracking-widest animate-pulse">
                  Querying Database...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="px-4 py-10 text-center text-slate-400 text-xs font-black tracking-widest">
                  No locations with geometry found
                </div>
              ) : (
                filteredOptions.map((opt) => (
                  <div
                    key={`${opt.type}-${opt.code}`}
                    className="px-6 py-3.5 cursor-pointer hover:bg-green-50 text-sm transition-colors border-b border-slate-50 last:border-none flex items-center justify-between group"
                    onClick={() => {
                      onChange(opt);
                      setIsOpen(false);
                      setSearchQuery("");
                    }}
                  >
                    <span className="font-semibold text-slate-700 group-hover:text-green-700">{opt.name}</span>
                    <span className="text-[10px] font-black px-2 py-0.5 bg-slate-100 text-slate-500 rounded-md group-hover:bg-green-100 group-hover:text-green-600 uppercase">
                      {opt.code}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </DropdownPortal>
      )}
    </div>
  );
};
// Main Component 

export const LocationCascadeSearch = ({ selectedLocations, onLocationsChange }) => {
  // Restore internal state from the parent on first mount.
  const findByType = (type) =>
    (selectedLocations || []).find((l) => l.type === type) || null;

  const [selectedState, setSelectedState] = useState(() => findByType("state"));
  const [selectedDistrict, setSelectedDistrict] = useState(() => findByType("district"));
  const [selectedGovDistrict, setSelectedGovDistrict] = useState(() => findByType("gov_district"));
  const [selectedMunicipality, setSelectedMunicipality] = useState(() => findByType("municipality"));
  const [selectedRegionalKey, setSelectedRegionalKey] = useState(() => findByType("regional_key"));

  const [options, setOptions] = useState({ states: [], districts: [], gov: [], mun: [], reg: [] });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetchStates().then(states => {
      setOptions(prev => ({ ...prev, states }));
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedState?.code) {
      setOptions(p => ({ ...p, districts: [], gov: [], mun: [], reg: [] }));
      return;
    }
    setIsLoading(true);
    const code = selectedState.code;
    Promise.all([
      API.get(`/locations/districts/${code}`),
      API.get(`/locations/govdistricts/${code}`),
      API.get(`/locations/municipalities/${code}`),
      API.get(`/locations/regionalkeys/${code}`),
    ]).then(([d, g, m, r]) => {
      setOptions(prev => ({
        ...prev,
        districts: d.data.map(i => ({ ...i, type: 'district' })),
        gov: g.data.map(i => ({ ...i, type: 'gov_district' })),
        mun: m.data.map(i => ({ ...i, type: 'municipality' })),
        reg: r.data.map(i => ({ ...i, type: 'regional_key' })),
      }));
      setIsLoading(false);
    });
  }, [selectedState?.code]);

  useEffect(() => {
    const locs = [];
    if (selectedState) locs.push(selectedState);
    const sub = selectedDistrict || selectedGovDistrict || selectedMunicipality || selectedRegionalKey;
    if (sub) locs.push(sub);
    onLocationsChange(locs);
  }, [selectedState, selectedDistrict, selectedGovDistrict, selectedMunicipality, selectedRegionalKey]);

  const clearSub = () => {
    setSelectedDistrict(null); setSelectedGovDistrict(null); 
    setSelectedMunicipality(null); setSelectedRegionalKey(null);
  };

  // Get the most specific geometry
  const activeLocation = selectedLocations[selectedLocations.length - 1];

  return (
    <div className="space-y-10 w-full">
      <div className="space-y-3">
        <div className="flex items-center gap-2 ml-1">
          <ShieldCheck size={14} className="text-green-600" />
          <label className="text-[13px] font-black text-gray-900 uppercase tracking-[0.2em]">
           Federal State
          </label>
        </div>
        <SimpleDropdown
          placeholder="Choose State (e.g.Bayern or 09)"
          value={selectedState}
          onChange={(item) => { setSelectedState(item); clearSub(); }}
          onClear={() => { setSelectedState(null); clearSub(); }}
          options={options.states}
          loading={isLoading && options.states.length === 0}
        />
      </div>


      <AnimatePresence>
        {selectedState && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8 p-8 rounded-[3rem] bg-white border border-slate-200 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)]"
          >
            <div className="flex flex-col border-l-4 border-green-500 pl-4 py-1">
                <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Regional Refinement</h3>
                <p className="text-[10px] font-bold text-slate-400 uppercase">Select one specific sub-division type</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">Districts</h4>
                <SimpleDropdown placeholder="Search Districts..." value={selectedDistrict} options={options.districts} loading={isLoading}
                  onChange={(item) => { clearSub(); setSelectedDistrict(item); }} onClear={() => setSelectedDistrict(null)} />
              </div>
              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">Government Districts</h4>
                <SimpleDropdown placeholder="Search Gov Districts..." value={selectedGovDistrict} options={options.gov} loading={isLoading}
                  onChange={(item) => { clearSub(); setSelectedGovDistrict(item); }} onClear={() => setSelectedGovDistrict(null)} />
              </div>
              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">Municipalities</h4>
                <SimpleDropdown placeholder="Search Municipalities..." value={selectedMunicipality} options={options.mun} loading={isLoading}
                  onChange={(item) => { clearSub(); setSelectedMunicipality(item); }} onClear={() => setSelectedMunicipality(null)} />
              </div>
              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">Region</h4>
                <SimpleDropdown placeholder="Search Regions..." value={selectedRegionalKey} options={options.reg} loading={isLoading}
                  onChange={(item) => { clearSub(); setSelectedRegionalKey(item); }} onClear={() => setSelectedRegionalKey(null)} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeLocation && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative pt-6 border-t border-slate-100"
          >
            <div className="flex items-center justify-between mb-4">
               <div className="flex items-center gap-2">
                  <Database size={16} className="text-green-600" />
                  <span className="text-[13px] font-black text-gray-900 uppercase tracking-[0.2em]">System Defined Geometry (Read-Only)</span>
               </div>
               <span className="text-[10px] font-bold bg-green-100 text-green-700 px-3 py-1 rounded-full uppercase">
                 Active: {activeLocation.name}
               </span>
            </div>
            
            <div className="relative group">
              <textarea
                readOnly
                value={activeLocation.geometry || "No geometry data available for this selection."}
                className="w-full p-6 bg-slate-900 text-green-400 font-mono text-[11px] rounded-[2rem] border-2 border-slate-800 focus:outline-none leading-relaxed shadow-xl cursor-default"
                rows={4}
              />
              <div className="absolute top-4 right-4 text-slate-600 opacity-20 group-hover:opacity-100 transition-opacity">
                 <MapPin size={40} />
              </div>
            </div>
            <p className="mt-3 ml-4 text-[10px] font-bold text-slate-400 uppercase italic">
              * This WKT string is automatically injected based on the official geospatial database records for the selected region.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
